<script setup>
import { Checkbox, DmButton, DmIconButton, ErrorHighlighter, Icon, StringInput } from 'dmui_components'
import { computed, ref, watch } from 'vue'

const props = defineProps({
	sectionData: {
		type: Array,
		default: () => []
	},
	sectionType: {
		type: String,
		default: '',
		validates: (value) => ['ceo', 'risk', 'gri'].includes(value)
	},
	validateSection: {
		type: Boolean,
		default: false
	},
	reportId: {
		type: Number,
		default: undefined
	},
	readonly: {
		type: Boolean,
		default: false
	},
	validationError: {
		type: String,
		default: ''
	}
})

const emit = defineEmits(['add:section-data', 'delete:section-data', 'update:errors'])

const sectionDataInput = ref([...props.sectionData])

const EMPTY_SECTION_ERROR = 'Section needs to be added or marked as not identified'
const MISSING_VALIDATION_ERROR = 'All inputs must be validated before saving'
const showValidationError = computed(() => props.validationError?.includes(props.sectionType))

const checkedState = ref(!!props.reportId)
const errorState = computed(() => (!props.sectionData?.length ? !checkedState.value : false))

function lastInputTouched(section) {
	if (section.length) {
		return section[section.length - 1].start !== '' && section[section.length - 1].end !== ''
	}
	return false
}
const hasPendingSections = computed(() => sectionDataInput.value?.length > props.sectionData?.length && lastInputTouched(sectionDataInput.value))
const showError = computed(() => props.validateSection && (errorState.value || hasPendingSections.value))

const sectionErrors = ref([])
const validatedState = ref(props.sectionData.map(() => true))

function sectionIsEqual(section1, section2) {
	return section1.start === section2.start && section1.end === section2.end && section1.section_type === section2.section_type
}

function validate(index, path) {
	const currentSection = sectionDataInput.value[index]
	if (props.sectionData?.findIndex((s) => sectionIsEqual(s, currentSection)) !== -1) {
		sectionErrors.value[index] = [{ path, message: 'Duplicated section' }]
		return false
	} else if (currentSection?.[path]) {
		if (currentSection['end'] < currentSection['start']) {
			sectionErrors.value[index] = [{ path, message: "End page can't be lesser than start page" }]
			return false
		}
		return true
	} else {
		sectionErrors.value[index] = [{ path, message: "Values can't be empty" }]
		return false
	}
}

function onClickValidate(index) {
	sectionErrors.value[index] = []
	validatedState.value[index] = validate(index, 'start') && validate(index, 'end')
	if (sectionErrors.value[index].length === 0) {
		emit('add:section-data', { type: props.sectionType, data: sectionDataInput.value[index] })
	}
}

function addRow() {
	const nextIndex = sectionDataInput.value.length
	if (nextIndex === 0 || validatedState.value[nextIndex - 1]) {
		sectionDataInput.value.push({ start: '', end: '', section_type: props.sectionType })
	} else {
		sectionErrors.value[nextIndex - 1] = [{ message: 'Row must be validated before adding new' }]
	}
}

function deleteRow(index) {
	sectionDataInput.value.splice(index, 1)
	validatedState.value.splice(index, 1)
	sectionErrors.value.splice(index, 1)
	checkedState.value = false
}

function deleteValidatedRow(index) {
	emit('delete:section-data', { type: props.sectionType, data: sectionDataInput.value[index] })
	deleteRow(index)
}

watch(hasPendingSections, () => emit('update:errors', { type: props.sectionType, error: hasPendingSections.value }))
watch(
	() => [props.sectionData, checkedState.value],
	() => emit('update:errors', { type: props.sectionType, error: errorState.value }),
	{ immediate: true }
)
</script>
<template>
	<section class="section-wrapper">
		<div class="section-content bordered-content" :class="{ error: showError || showValidationError }">
			<div v-if="sectionDataInput.length" class="section-data-table">
				<div class="page-details-header secondary-text bold">
					<div>Page start</div>
					<div>Page end</div>
					<DmButton id="add-new-row" class="transparent slim" :disabled="readonly" @click="addRow"> <Icon name="uil-plus" size="midi" /> Add </DmButton>
				</div>
				<template v-for="(details, index) in sectionDataInput" :key="index">
					<ErrorHighlighter :id="`error-highlighter-${index}`" :errors="sectionErrors[index]">
						<StringInput
							:data="details.start.toString()"
							appearance="transparent"
							class="transparent"
							placeholder="+ Add"
							:readonly="validatedState[index]"
							:focus-on-view="!validatedState[index]"
							@update:data="details.start = Number($event)" />
						<StringInput
							:data="details.end.toString()"
							appearance="transparent"
							class="transparent"
							placeholder="+ Add"
							:readonly="validatedState[index]"
							@update:data="details.end = Number($event)" />
						<div class="icon-cell">
							<DmIconButton v-if="!validatedState[index]" id="validate" class="primary transparent slim" icon="uil-check" icon-size="midi" @click="onClickValidate(index)">
								Validate
							</DmIconButton>
							<DmIconButton v-if="!validatedState[index]" id="cancel" class="danger transparent slim" icon="uil-times" icon-size="midi" @click="deleteRow(index)">
								Cancel
							</DmIconButton>
							<DmIconButton
								v-if="validatedState[index] && !readonly"
								id="delete"
								class="secondary transparent slim"
								icon="uil-trash-alt"
								icon-size="midi"
								@click="deleteValidatedRow(index)">
								Delete
							</DmIconButton>
						</div>
					</ErrorHighlighter>
				</template>
			</div>
			<div v-else class="header">
				<DmButton :id="`add-${sectionType}-section`" class="transparent" :disabled="readonly" @click="addRow"> <Icon name="uil-plus" size="midi" /> Add section </DmButton>
				<label v-if="!readonly" class="secondary-text" :for="`${sectionType}-not-identified`">
					<Checkbox :id="`${sectionType}-not-identified`" v-model:data="checkedState" />
					<span>Not identified</span>
				</label>
			</div>
		</div>
		<span v-if="showError" class="secondary-text error-message">{{ hasPendingSections ? MISSING_VALIDATION_ERROR : EMPTY_SECTION_ERROR }} </span>
		<span v-else-if="showValidationError" class="secondary-text error-message">{{ validationError }} </span>
	</section>
</template>

<style scoped lang="less">
.section-content {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: @medium;
}
.header {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

label {
	display: flex;
	align-items: center;
	gap: @micro;
}
.section-wrapper {
	display: flex;
	flex-direction: column;
}
.error-message {
	margin-top: 2px;
	color: @dm-wine;
}
.icon-header {
	display: flex;
	justify-content: flex-start;
}
.icon-cell {
	display: flex;
	justify-content: flex-end;
	gap: @micro;
}

.page-details-header,
:deep(.container) {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	align-items: center;
	column-gap: @midi;
	padding-bottom: @micro;
}
.page-details-header {
	border-bottom: 1px solid @gray-50;
	margin-bottom: 19px; //for consistency with error-highlighter
}

:deep(.container) {
	border-bottom: 1px solid @gray-30;
}
</style>
